import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getAccountStatus } from '../services/api';
import AccountDeletion from './AccountDeletion';
import './DeletionWarningBanner.css';

function DeletionWarningBanner() {
  const { user } = useSelector((state) => state.auth);
  const [accountStatus, setAccountStatus] = useState(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const loadAccountStatus = useCallback(async () => {
    try {
      const response = await getAccountStatus();
      setAccountStatus(response.data);
    } catch (err) {
      console.error('Failed to load account status:', err);
    }
  }, []);

  // Depend on ``user?.id`` (primitive) — see Dashboard.js for the
  // rationale. Avoids a refire on every Redux ``/auth/me`` dispatch.
  const userId = user?.id;
  useEffect(() => {
    if (userId) {
      loadAccountStatus();
    }
  }, [userId, loadAccountStatus]);

  // Listen for account deletion events to refresh status
  useEffect(() => {
    const handleDeletionScheduled = () => {
      loadAccountStatus();
    };

    const handleDeletionCancelled = () => {
      loadAccountStatus();
    };

    window.addEventListener('accountDeletionScheduled', handleDeletionScheduled);
    window.addEventListener('accountDeletionCancelled', handleDeletionCancelled);

    return () => {
      window.removeEventListener('accountDeletionScheduled', handleDeletionScheduled);
      window.removeEventListener('accountDeletionCancelled', handleDeletionCancelled);
    };
  }, [loadAccountStatus]);

  // Add class to body when banner is visible to add padding
  useEffect(() => {
    if (accountStatus?.has_active_deletion_request) {
      document.body.classList.add('has-deletion-banner');
    } else {
      document.body.classList.remove('has-deletion-banner');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('has-deletion-banner');
    };
  }, [accountStatus?.has_active_deletion_request]);

  if (!accountStatus?.has_active_deletion_request) {
    return null;
  }

  const deletionDate = accountStatus?.deletion_request?.scheduled_deletion_at
    ? new Date(accountStatus.deletion_request.scheduled_deletion_at)
    : null;
  
  const daysRemaining = deletionDate
    ? Math.ceil((deletionDate - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <>
      <div className="deletion-warning-banner">
        <div className="banner-content">
          <div className="banner-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="banner-text">
            <strong>Account Scheduled for Deletion</strong>
            <span>
              Your account will be permanently deleted on{' '}
              <strong>{deletionDate ? deletionDate.toLocaleDateString() : 'N/A'}</strong>
              {daysRemaining !== null && daysRemaining > 0 && (
                <> ({daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining)</>
              )}
            </span>
          </div>
          <div className="banner-actions">
            <button
              className="btn-cancel-deletion"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel Deletion
            </button>
            <button
              className="btn-view-details"
              onClick={() => setShowDeletionModal(true)}
            >
              View Details
            </button>
          </div>
        </div>
      </div>

      <AccountDeletion
        key="deletion-modal"
        isOpen={showDeletionModal}
        onClose={() => {
          setShowDeletionModal(false);
          // Refresh account status when modal closes (in case deletion was canceled)
          loadAccountStatus();
        }}
        initialStep="scheduled"
        initialScheduledDate={accountStatus?.deletion_request?.scheduled_deletion_at}
      />

      <AccountDeletion
        key="cancel-modal"
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          // Refresh account status when modal closes (in case deletion was canceled)
          loadAccountStatus();
        }}
        initialStep="cancelConfirm"
        initialScheduledDate={accountStatus?.deletion_request?.scheduled_deletion_at}
      />
    </>
  );
}

export default DeletionWarningBanner;

